import TextField from "@/atoms/TextField";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "@/atoms/Button";
import Select from "@/atoms/Select";
import { useContext, useState } from "react";
import { US_STATES } from "constants/states";
import { submitFormik } from "utils/forms";
import { isEqual } from "lodash";
import getStateFromZip from "utils/getStateFromZip";
import { SubscriptionsContext } from "contexts/SubscriptionsProvider";
import { CardTitle } from "components/pages/account/typography";
import Divider from "@/atoms/account/Divider";
import Card from "@/atoms/account/Card";
import { validateAddress } from "lib/api/subscription";
import styled from "styled-components";

const ButtonsWrapper = styled.div`
  display: grid;
  grid-column-gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
`;

const SuggestedAddressForm = ({
  submittedAddress,
  suggestedAddress,
  onContinue,
  onReject,
  isSubmitting,
}) => {
  return (
    <div className={"text-sm"}>
      <div className={"text-black mb-4 font-bold"}>
        <div>{submittedAddress?.street1}</div>
        <div>{submittedAddress?.street2}</div>
        <div>
          {submittedAddress?.city}, {submittedAddress?.state}{" "}
          {submittedAddress?.postalCode}
        </div>
      </div>
      <p>
        The shipping address you entered does not match the suggested address
        provided by the United States Postal Service.
      </p>
      <div
        className={
          "font-medium mt-3 mb-2 uppercase text-xxs text-textGray-lighter tracking-wider"
        }
      >
        Suggested Address
      </div>
      <div className={"bg-pulp-white rounded-md px-md py-sm text-black"}>
        <div>{suggestedAddress?.street1}</div>
        <div>{suggestedAddress?.street2}</div>
        <div>
          {suggestedAddress?.city}, {suggestedAddress?.state}{" "}
          {suggestedAddress?.postalCode}
        </div>
      </div>
      <ButtonsWrapper className={"grid gap-4 mt-4"}>
        <Button className={"w-full"} onClick={onReject} level={"secondary"}>
          Edit Address
        </Button>
        <Button
          className={"w-full"}
          onClick={onContinue}
          loading={isSubmitting}
          color={"teal"}
        >
          Continue with this address
        </Button>
      </ButtonsWrapper>
    </div>
  );
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required."),
  street1: Yup.string().required("Required."),
  city: Yup.string().required("Required."),
  state: Yup.string().required("Required."),
  postalCode: Yup.string().required("Required."),
});

export const ShippingAddress = ({
  shippingAddress,
  onSuccess = () => null,
  onSave,
  onIneligibleAddress,
  className,
  withContinue,
  children,
  raw,
  cardTitle,
  ctaLabel,
  submitPristine,
  ...props
}) => {
  const [submittedAddress, setSubmittedAddress] = useState();
  const [suggestedAddress, setSuggestedAddress] = useState();

  const handleIneligibleAddressError = (res, address) => {
    // parse response and notify parent of ineligible address
    if (
      res?.error?.errors?.[0]?.code === "address_not_eligible" &&
      onIneligibleAddress
    ) {
      onIneligibleAddress(address);
    }
  };

  const onSubmit = async (values, formikProps) => {
    await submitFormik({
      onSubmit: async () => {
        const res = await props.onSubmit(values);
        handleIneligibleAddressError(res, values);
        return res;
      },
      onSuccess: async (res) => {
        const { isValid, submittedAddress, suggestedAddress } = res;
        if (isValid) {
          const res = await onSave(submittedAddress);
          handleIneligibleAddressError(res, submittedAddress);
          return onSuccess();
        }
        if (suggestedAddress) {
          setSubmittedAddress(submittedAddress);
          setSuggestedAddress(suggestedAddress);
        }
      },
      formikProps,
    });
  };

  const initialShippingAddress = shippingAddress
    ? shippingAddress
    : {
        name: "",
        street1: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
      };

  const Container = raw ? "div" : Card;
  return (
    <Container className={className}>
      {!raw && (
        <>
          <CardTitle>{cardTitle || "Shipping Address"}</CardTitle>
          <Divider className={"my-4"} />
        </>
      )}
      <Formik
        initialValues={initialShippingAddress}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({
          dirty,
          isSubmitting,
          values,
          setValues,
          handleSubmit,
          errors,
          resetForm,
          getFieldMeta,
          setTouched,
          touched,
          isValid,
        }) => {
          const getError = (field) => {
            const meta = getFieldMeta(field);
            return meta?.touched && meta?.error;
          };

          const submitIfChanged = (e) => {
            // or if the address is default and not applied to the item yet
            e.preventDefault();

            if (submitPristine) {
              handleSubmit();
            } else {
              dirty || !shippingAddress ? handleSubmit() : onSuccess();
            }
          };

          const handleSuggestedAddress = async (e) => {
            e.preventDefault();
            resetForm({ values: suggestedAddress });
            setSuggestedAddress(null);
            // handle edge case where the suggestion may match the already applied address
            const suggestedMatched = isEqual(
              suggestedAddress,
              props.shippingAddress
            );
            if (!suggestedMatched) {
              const res = await onSave(suggestedAddress);
              // since this is a validated address, we don't expect field-level errors, however
              // there may be ineleigible address errors
              handleIneligibleAddressError(res, suggestedAddress);
            }
            onSuccess();
          };

          const handlePostalChange = (e) => {
            const postalCode = e.target.value;
            const { st } = getStateFromZip(postalCode);
            setValues({ ...values, postalCode, state: st || values.state });
          };

          return (
            <Form onSubmit={submitIfChanged}>
              {suggestedAddress ? (
                <SuggestedAddressForm
                  submittedAddress={submittedAddress}
                  suggestedAddress={suggestedAddress}
                  onContinue={handleSuggestedAddress}
                  onReject={(e) => {
                    e.preventDefault();
                    setSuggestedAddress(null);
                    resetForm();
                  }}
                  isSubmitting={isSubmitting}
                />
              ) : (
                <>
                  <Field
                    as={TextField}
                    name={"name"}
                    label={"Name"}
                    placeholder={"Enter shipping name"}
                    className={"w-full"}
                    helpText={
                      "Suggestion: Personalize deliveries by using the member's name here."
                    }
                    error={getError("name")}
                    autoComplete="new-password"
                  />
                  <div className={"grid md:grid-cols-2 gap-x-4"}>
                    <Field
                      as={TextField}
                      name={"street1"}
                      label={"Address"}
                      placeholder="Enter your address"
                      className={"w-full"}
                      error={getError("street1")}
                    />
                    <Field
                      as={TextField}
                      name={"street2"}
                      label={"Address 2"}
                      placeholder="(unit, suite, etc)"
                      className={"w-full"}
                      error={getError("street2")}
                    />
                  </div>
                  <div className={"grid md:grid-cols-2 gap-x-4"}>
                    <Field
                      as={TextField}
                      name={"city"}
                      label={"City"}
                      placeholder="Enter your city"
                      className={"w-full"}
                      error={getError("city")}
                    />
                    <div className={"grid grid-cols-2 gap-x-4"}>
                      <Select
                        id={"shipping-state"}
                        label={"State"}
                        onChange={(state) => {
                          setTouched({ ...touched, state: true });
                          setValues({ ...values, state: state.value });
                        }}
                        items={US_STATES}
                        itemToString={(i) => i.name}
                        selectedItem={
                          US_STATES.find((s) => s.value === values?.state) ||
                          null
                        }
                        btnWidth={"100%"}
                        error={getError("state")}
                      />
                      <Field
                        as={TextField}
                        name={"postalCode"}
                        label={"Zip Code"}
                        placeholder="Enter your zip code"
                        onChange={handlePostalChange}
                        error={getError("postalCode")}
                        className={"w-full"}
                      />
                    </div>
                  </div>

                  {errors.form && (
                    <div
                      className={"text-error mb-3 text-xs"}
                    >{`${errors.form} Please try again.`}</div>
                  )}
                  {!(raw || withContinue) && (
                    <div className={"flex justify-end"}>
                      <Button
                        label={ctaLabel || "Save Changes"}
                        loading={isSubmitting}
                        disabled={
                          submitPristine ? !isValid : !isValid || !dirty
                        }
                        type={"submit"}
                        color={"teal"}
                      />
                    </div>
                  )}
                </>
              )}

              {!suggestedAddress && children}
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

const ConnectedShippingAddress = (props) => {
  const { subscription, saveShippingAddress } =
    useContext(SubscriptionsContext);
  const { shippingAddress } = subscription;
  const handleSave = (address) =>
    saveShippingAddress({ address, subId: subscription.id });

  return (
    <ShippingAddress
      shippingAddress={shippingAddress}
      onSubmit={validateAddress}
      onSave={handleSave}
      {...props}
    />
  );
};

export default ConnectedShippingAddress;
